import { useMutation, UseMutationOptions } from 'react-query';
import makeAddToBasket from 'infrastructure/basket/addToBasket';
import { useBrandId, useAppConfig } from '@ux/whitelabel';
import { useRefreshBasketCount } from '@uds/bookends';
import { AddDomainToBasketType } from 'domain/constants/basket';
import { AddDomainToBasketArgs } from 'core/basket';
import { dispatchGA4Event } from 'infrastructure/ga4';
import { useCurrency } from '@ux/currency';

export interface AddToBasketProps {
  fqdn?: string;
  fqdnType?: string;
  resolve?: Function;
  reject?: Function;
  type: AddDomainToBasketType;
  fromBundle?: boolean;
  isLastBundleItem?: boolean;
  tld?: string;
  price?: number;
}

export interface AddToBasketResponse {
  success: boolean;
}

export const useAddDomainToBasket = (config?: UseMutationOptions<AddToBasketResponse, unknown, AddToBasketProps>) => {
  const refreshBasketCount = useRefreshBasketCount();
  const brandId = useBrandId();
  const currency = useCurrency();
  const customDomainTerms = useAppConfig('dst')?.customDomainTerms;

  const addToBasket = ({ fqdn = '', type, fromBundle = false, fqdnType }: AddToBasketProps) => {
    const tld = fqdn.substring(fqdn.indexOf('.') + 1);
    const isStandardDomain = fqdnType === 'standard';
    const isRegistration = type === AddDomainToBasketType.DOMAINS;
    const changeTermAndInterval = isStandardDomain && !fromBundle && isRegistration;
    const term = changeTermAndInterval ? customDomainTerms?.[tld]?.term : null;
    const interval = changeTermAndInterval ? customDomainTerms?.[tld]?.interval : null;
    const items: Array<AddDomainToBasketArgs> = [];
    items.push({ domainName: fqdn, type, term, paymentInterval: interval, quantity: 1 });

    return makeAddToBasket(brandId)({ items });
  };

  return useMutation(addToBasket, {
    ...config,
    onSuccess: (data, variables, _) => {
      refreshBasketCount();
      if (config?.onSuccess != null) {
        config.onSuccess(data, variables, _);
      }
      // Send GA4 ecommerce data
      const domainExtension = variables?.tld;
      const domainPrice = variables?.price;
      const domain = {
        'item_name': `Domain .${domainExtension}`,
        'item_brand': brandId,
        'item_category': 'Domains',
        'item_variant': `Domain .${domainExtension} 12`,
        'currency': currency,
        'price': domainPrice ? parseFloat(domainPrice.toFixed(2)) : 0.00,
        'quantity': 1,
      };
      dispatchGA4Event('view_item', {
        items: [ domain ],
      });
      dispatchGA4Event('add_to_cart', {
        items: [ domain ],
      });
    },
  });
};

export default useAddDomainToBasket;
